import type { DocumentLocation } from '@st/document-viewer-kit'
import { paginateChecklistItems, type FormationLayout } from '@st/formation'
import type { DocumentAnnotation, STDocument, STOpenFolderState } from '@st/sdk'
import { resolveViewerDocKind } from '~/st-doc-viewer/viewer-document-model'
import type { NavigationRequest, NavigationTarget } from './st-folder-types'

export function navigateToChecklist(phase: 'intake' | 'delivery'): NavigationRequest {
  return { documentId: 'checklist', target: { type: 'checklist', phase } }
}

export function navigateToDocument(documentId: string): NavigationRequest {
  return {
    documentId,
    target: { type: 'document' }
  }
}

export function navigateToPage(documentId: string, pageId: string): NavigationRequest {
  return {
    documentId,
    target: { type: 'page', pageId }
  }
}

export function navigateToAnnotation(documentId: string, annotationId: string): NavigationRequest {
  return {
    documentId,
    target: { type: 'annotation', annotationId }
  }
}

export function resolveDocumentLocationForTarget({
  folderState,
  formationLayout,
  doc,
  target
}: {
  folderState: STOpenFolderState
  formationLayout: FormationLayout
  doc: STDocument
  target: NavigationTarget
}): DocumentLocation | undefined {
  switch (target.type) {
    case 'checklist':
      return {
        pageNumber: paginateChecklistItems(folderState.checklistItems).find(
          (page) => page.phase == target.phase
        )!.number
      }
    case 'document':
      return undefined
    case 'page':
      return resolveDocumentLocationForBookmark({
        formationLayout,
        doc,
        pageId: target.pageId
      })
    case 'annotation':
      return resolveDocumentLocationForAnnotation({
        folderState,
        formationLayout,
        doc,
        annotationId: target.annotationId
      })
  }
}

export function resolveDocumentLocationForBookmark({
  formationLayout,
  doc,
  pageId
}: {
  formationLayout: FormationLayout
  doc: STDocument
  pageId: string
}): DocumentLocation | undefined {
  let pageNumber = resolvePageNumber(pageId)
  if (resolveViewerDocKind(doc.mimeType) == 'formation') {
    pageNumber = formationLayout.pageNumberByPageId[pageId]
  }

  if (pageNumber == undefined) {
    return undefined
  }

  return { pageNumber }
}

function resolveDocumentLocationForAnnotation({
  folderState,
  formationLayout,
  doc,
  annotationId
}: {
  folderState: STOpenFolderState
  formationLayout: FormationLayout
  doc: STDocument
  annotationId: string
}): DocumentLocation | undefined {
  const comment = folderState.comments.find(
    (comment) => comment.documentId == doc.id && comment.id == annotationId
  )

  if (comment?.anchor != undefined) {
    if (resolveViewerDocKind(doc.mimeType) != 'formation') return undefined

    const pageNumber = formationLayout.pageNumberByAnchor[comment.anchor]
    if (pageNumber == undefined) return undefined
    return { pageNumber }
  }

  const annotation = doc.annotations?.[annotationId] as DocumentAnnotation | undefined
  const pageNumber = comment?.pageNumber ?? annotation?.pageNumber
  const bounds = comment?.bounds ?? annotation?.bounds

  if (!Number.isInteger(pageNumber) || pageNumber == undefined || pageNumber <= 0 || !bounds) {
    return undefined
  }

  const docKind = resolveViewerDocKind(doc.mimeType)

  switch (docKind) {
    case 'formation-checklist':
      return undefined
    case 'formation': {
      const visiblePageCount = formationLayout.pages.length

      if (pageNumber > visiblePageCount) {
        return undefined
      }

      return {
        pageNumber,
        bounds
      }
    }
    case 'none':
      return undefined
    case 'pdf':
    default: {
      if ((doc.deletedPageNumbers ?? []).includes(pageNumber)) {
        return undefined
      }
      if (typeof doc.pageCount == 'number' && pageNumber > doc.pageCount) {
        return undefined
      }

      return {
        pageNumber,
        bounds
      }
    }
  }
}

function resolvePageNumber(pageId: string): number | undefined {
  const pageNumber = Number(pageId)
  if (!Number.isInteger(pageNumber) || pageNumber <= 0) {
    return undefined
  }
  return pageNumber
}
